import React, { useState } from 'react';
import axios from 'axios';
import { usePrefs } from '../hooks/usePrefs';

const API = process.env.REACT_APP_API_URL || '';
const ax  = axios.create({ baseURL: API, withCredentials: true });

function ScoreCircle({ score }) {
  const c = score >= 70 ? '#4ade80' : score >= 40 ? '#fbbf24' : '#f87171';
  const label = score >= 70 ? 'BON' : score >= 40 ? 'MOYEN' : 'FAIBLE';
  return (
    <div style={{ position:'relative', width:130, height:130, flexShrink:0 }}>
      <svg width="130" height="130" style={{ transform:'rotate(-90deg)' }}>
        <circle cx="65" cy="65" r="56" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10"/>
        <circle cx="65" cy="65" r="56" fill="none" stroke={c} strokeWidth="10"
          strokeDasharray={`${2 * Math.PI * 56}`}
          strokeDashoffset={`${2 * Math.PI * 56 * (1 - score / 100)}`}
          strokeLinecap="round" style={{ transition:'stroke-dashoffset 1s ease' }}/>
      </svg>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
        <span style={{ fontSize:'2rem', fontWeight:900, color:c, lineHeight:1 }}>{score}</span>
        <span style={{ fontSize:'0.55rem', color:c+'99', fontWeight:700, letterSpacing:'0.08em' }}>/100</span>
        <span style={{ fontSize:'0.55rem', color:c, fontWeight:800, letterSpacing:'0.1em', marginTop:2 }}>{label}</span>
      </div>
    </div>
  );
}

function PSBar({ score, label }) {
  if (score == null) return null;
  const c = score >= 70 ? '#4ade80' : score >= 40 ? '#fbbf24' : '#f87171';
  return (
    <div style={{ flex:1 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
        <span style={{ color:'#888', fontSize:'0.75rem' }}>{label}</span>
        <span style={{ color:c, fontWeight:800, fontSize:'0.82rem' }}>{score}/100</span>
      </div>
      <div style={{ height:7, background:'rgba(255,255,255,0.06)', borderRadius:99, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${score}%`, background:c, borderRadius:99, transition:'width 1s ease' }}/>
      </div>
    </div>
  );
}

function Check({ ok, label, detail, warn }) {
  const col = ok ? '#4ade80' : warn ? '#fbbf24' : '#f87171';
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'9px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize:14, flexShrink:0, marginTop:1 }}>{ok ? '✅' : warn ? '⚠️' : '❌'}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ color: ok ? '#888' : '#EAEAEA', fontSize:'0.8rem', fontWeight: ok ? 400 : 600 }}>{label}</div>
        {detail && <div style={{ color:'#444', fontSize:'0.7rem', marginTop:2, wordBreak:'break-word' }}>{detail}</div>}
      </div>
    </div>
  );
}

function generateFixes(d) {
  const t = d.suggestions?.optimizedTitle || d.title || 'Titre de votre entreprise';
  const m = d.suggestions?.optimizedMeta  || d.metaDesc || 'Description de votre activité...';
  const fixes = [];

  if (!d.title || d.titleLen < 40 || d.titleLen > 70)
    fixes.push({ key:'title', label:'Balise <title>', icon:'📝',
      code:`<title>${t}</title>`,
      where:'Remplacez votre <title> existant dans le <head> de chaque page.' });

  if (!d.metaDesc || d.metaLen < 100 || d.metaLen > 160)
    fixes.push({ key:'meta', label:'Meta description', icon:'📋',
      code:`<meta name="description" content="${m}">`,
      where:'Ajoutez dans le <head>. Adaptez la description pour chaque page.' });

  if (!d.hasViewport)
    fixes.push({ key:'viewport', label:'Viewport mobile', icon:'📱',
      code:`<meta name="viewport" content="width=device-width, initial-scale=1">`,
      where:'Ajoutez dans le <head>. Indispensable pour l\'indexation mobile-first Google.' });

  if (!d.canonical)
    fixes.push({ key:'canonical', label:'URL canonique', icon:'🔗',
      code:`<link rel="canonical" href="${d.url}">`,
      where:'Adaptez l\'URL pour chaque page. Prévient le duplicate content.' });

  if (!d.hasOGTitle || !d.hasOGDesc || !d.hasOGImage)
    fixes.push({ key:'og', label:'Open Graph (réseaux sociaux)', icon:'📣',
      code:`<meta property="og:title" content="${t}">\n<meta property="og:description" content="${m}">\n<meta property="og:url" content="${d.url}">\n<meta property="og:type" content="website">\n<meta property="og:image" content="${d.url}/og-image.jpg">`,
      where:'Ajoutez dans le <head>. Contrôle l\'aperçu sur Facebook, LinkedIn, WhatsApp, etc.' });

  if (!d.hasSchema)
    fixes.push({ key:'schema', label:'Données structurées (Schema.org)', icon:'🗂️',
      code:`<script type="application/ld+json">\n{\n  "@context": "https://schema.org",\n  "@type": "LocalBusiness",\n  "name": "${t}",\n  "url": "${d.url}"\n}\n</script>`,
      where:'Ajoutez avant </head>. Améliore l\'affichage dans les résultats Google (rich snippets).' });

  if (!d.hasAnalytics)
    fixes.push({ key:'analytics', label:'Google Analytics 4', icon:'📊',
      code:`<!-- Remplacez G-XXXXXXXX par votre ID Google Analytics -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-XXXXXXXX');\n</script>`,
      where:'1. Créez un compte sur analytics.google.com\n2. Copiez votre ID (G-XXXXXXXX)\n3. Ajoutez dans le <head> de chaque page.' });

  if (!d.hasRobots)
    fixes.push({ key:'robots', label:'Robots.txt', icon:'🤖',
      code:`User-agent: *\nAllow: /\n\nSitemap: ${d.url}/sitemap.xml`,
      where:'Créez un fichier robots.txt à la racine de votre serveur (ex: /public/robots.txt).' });

  if (!d.hasSitemap)
    fixes.push({ key:'sitemap', label:'Sitemap XML', icon:'🗺️',
      code:`<!-- WordPress : installez le plugin Yoast SEO ou Rank Math -->\n<!-- Autres CMS : générez sur https://www.xml-sitemaps.com -->\n<!-- Puis soumettez sur https://search.google.com/search-console -->`,
      where:'Générez votre sitemap.xml et déposez-le à la racine du site. Soumettez ensuite dans Google Search Console.' });

  return fixes;
}

function generateCompleteHead(d) {
  const t = d.suggestions?.optimizedTitle || d.title || 'Titre';
  const m = d.suggestions?.optimizedMeta  || d.metaDesc || '';
  return `<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${t}</title>
  <meta name="description" content="${m}">
  <link rel="canonical" href="${d.url}">

  <!-- Open Graph -->
  <meta property="og:title" content="${t}">
  <meta property="og:description" content="${m}">
  <meta property="og:url" content="${d.url}">
  <meta property="og:type" content="website">
  <meta property="og:image" content="${d.url}/og-image.jpg">

  <!-- Schema.org LocalBusiness -->
  <script type="application/ld+json">
  {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "${t}",
    "url": "${d.url}"
  }
  </script>

  <!-- Google Analytics GA4 — remplacez G-XXXXXXXX -->
  <!-- <script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXX"></script>
  <script>
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-XXXXXXXX');
  </script> -->

  <!-- Vos styles et autres balises ici -->
</head>`;
}

export default function SEOSection() {
  const { prefs } = usePrefs();
  const [url,        setUrl]        = useState('');
  const [loading,    setLoading]    = useState(false);
  const [data,       setData]       = useState(null);
  const [error,      setError]      = useState(null);
  const [copied,     setCopied]     = useState(false);
  const [showFixes,  setShowFixes]  = useState(false);
  const [copiedFix,  setCopiedFix]  = useState(null);
  const [viewTab,    setViewTab]    = useState('tool');
  const [activity,   setActivity]   = useState(null);
  const [actLoading, setActLoading] = useState(false);

  async function analyze() {
    if (!url.trim()) return;
    setLoading(true); setError(null); setData(null);
    try {
      const { data: d } = await ax.post('/api/seo/analyze', { url: url.trim() });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }

  async function loadActivity() {
    if (activity) return;
    setActLoading(true);
    const [r1, r2, r3] = await Promise.allSettled([
      ax.get('/api/audit/list'),
      ax.get('/api/audit/website-orders'),
      ax.get('/api/audit/free-scans'),
    ]);
    setActivity({
      audits:   r1.status === 'fulfilled' ? r1.value.data : [],
      websites: r2.status === 'fulfilled' ? r2.value.data : [],
      scans:    r3.status === 'fulfilled' ? r3.value.data : [],
    });
    setActLoading(false);
  }

  function switchTab(tab) {
    setViewTab(tab);
    if (tab === 'activity') loadActivity();
  }

  function exportPDF() {
    if (!data) return;
    const d = data;
    const agency = prefs.agencyName || 'Ucreative Inc.';
    const scoreColor = d.score >= 70 ? '#16a34a' : d.score >= 40 ? '#d97706' : '#dc2626';
    const psM = d.pagespeed?.mobile;
    const psD = d.pagespeed?.desktop;

    const checkRows = [
      { ok: d.isHttps,                  label:'HTTPS sécurisé',              detail: d.isHttps ? 'Actif' : '❌ Site non sécurisé — pénalisé par Google' },
      { ok: d.title && d.titleLen>=40 && d.titleLen<=70, label:'Balise titre SEO', detail: d.title ? `"${d.title}" (${d.titleLen} car.)` : 'Absente' },
      { ok: d.metaDesc && d.metaLen>=100 && d.metaLen<=160, label:'Meta description', detail: d.metaDesc ? `"${d.metaDesc?.slice(0,80)}…" (${d.metaLen} car.)` : 'Absente' },
      { ok: d.h1s?.length===1,           label:'Balise H1',                   detail: d.h1s?.length ? `"${d.h1s[0]}"` : 'Absente' },
      { ok: d.imgTotal===0 || d.imgWithAlt/d.imgTotal>=0.9, label:'Alt des images', detail: `${d.imgWithAlt}/${d.imgTotal} images optimisées` },
      { ok: d.hasViewport,               label:'Mobile-friendly (viewport)',   detail: d.hasViewport ? 'Présent' : 'Absent — site non adapté mobile' },
      { ok: !!d.canonical,               label:'URL canonique',                detail: d.canonical || 'Absente — risque de contenu dupliqué' },
      { ok: d.hasOGTitle && d.hasOGDesc, label:'Open Graph (réseaux sociaux)', detail: d.hasOGTitle ? 'Configuré' : 'Absent — partages sans aperçu' },
      { ok: d.hasSchema,                 label:'Données structurées (Schema)', detail: d.hasSchema ? 'Détecté' : 'Absent — moins de rich snippets Google' },
      { ok: d.hasAnalytics,              label:'Google Analytics / GTM',       detail: d.hasAnalytics ? 'Installé' : 'Absent — trafic non mesuré' },
      { ok: d.hasRobots,                 label:'Robots.txt',                   detail: d.hasRobots ? 'Présent' : 'Absent' },
      { ok: d.hasSitemap,                label:'Sitemap XML',                  detail: d.hasSitemap ? 'Présent' : 'Absent — pages mal indexées' },
    ];

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Rapport SEO — ${d.url}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,Arial,sans-serif;max-width:800px;margin:0 auto;padding:48px 40px;color:#111;line-height:1.5}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;padding-bottom:24px;border-bottom:2px solid #e5e7eb}
.score{font-size:56px;font-weight:900;color:${scoreColor};line-height:1}
h2{font-size:13px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.08em;margin:24px 0 12px;padding-bottom:6px;border-bottom:1px solid #f3f4f6}
.check{display:flex;gap:10;padding:8px 0;border-bottom:1px solid #f9fafb}
.ps-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:8px}
.ps-card{background:#f9fafb;border-radius:8px;padding:14px}
.ps-score{font-size:32px;font-weight:900;margin-bottom:2px}
.ai-box{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:14px;margin:8px 0}
.kw{display:inline-block;background:#e5e7eb;border-radius:4px;padding:3px 8px;font-size:12px;margin:3px;font-weight:600}
.footer{margin-top:40px;padding-top:16px;border-top:1px solid #e5e7eb;color:#888;font-size:11px;display:flex;justify-content:space-between}
@media print{body{padding:20px}}
</style></head><body>
<div class="header">
  <div>
    <div style="font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:#888;margin-bottom:6px">${agency}</div>
    <h1 style="font-size:20px;font-weight:800;margin-bottom:4px">Rapport SEO complet</h1>
    <div style="color:#2563eb;font-size:13px">${d.url}</div>
  </div>
  <div style="text-align:right">
    <div class="score">${d.score}/100</div>
    <div style="font-size:12px;color:#888;margin-top:4px">Score SEO global</div>
  </div>
</div>

${psM || psD ? `<h2>Performance (PageSpeed Google)</h2>
<div class="ps-grid">
  ${psM ? `<div class="ps-card"><div style="font-size:12px;color:#888;margin-bottom:4px">📱 Mobile</div><div class="ps-score" style="color:${psM.score>=70?'#16a34a':psM.score>=40?'#d97706':'#dc2626'}">${psM.score}/100</div>${psM.fcp?`<div style="font-size:12px;color:#555">FCP: ${psM.fcp}${psM.lcp?' · LCP: '+psM.lcp:''}</div>`:''}</div>` : ''}
  ${psD ? `<div class="ps-card"><div style="font-size:12px;color:#888;margin-bottom:4px">💻 Desktop</div><div class="ps-score" style="color:${psD.score>=70?'#16a34a':psD.score>=40?'#d97706':'#dc2626'}">${psD.score}/100</div>${psD.fcp?`<div style="font-size:12px;color:#555">FCP: ${psD.fcp}${psD.lcp?' · LCP: '+psD.lcp:''}</div>`:''}</div>` : ''}
</div>` : ''}

<h2>Audit technique (${checkRows.filter(r=>r.ok).length}/${checkRows.length} points validés)</h2>
${checkRows.map(r=>`<div class="check"><span style="font-size:14px">${r.ok?'✅':'❌'}</span><div><div style="font-size:13px;font-weight:${r.ok?400:600}">${r.label}</div><div style="font-size:11px;color:#888">${r.detail}</div></div></div>`).join('')}

${data.suggestions ? `<h2>Suggestions IA (Claude)</h2>
<div class="ai-box"><strong style="font-size:12px;color:#166534">TITRE OPTIMISÉ</strong><br><div style="font-size:14px;margin-top:4px">${data.suggestions.optimizedTitle || ''}</div></div>
<div class="ai-box"><strong style="font-size:12px;color:#166534">META DESCRIPTION OPTIMISÉE</strong><br><div style="font-size:13px;margin-top:4px">${data.suggestions.optimizedMeta || ''}</div></div>
${data.suggestions.keywords?.length ? `<h2>Mots-clés cibles</h2><div>${data.suggestions.keywords.map(k=>`<span class="kw">${k}</span>`).join('')}</div>` : ''}
${data.suggestions.recommendations?.length ? `<h2>Recommandations prioritaires</h2><ol style="padding-left:18px">${data.suggestions.recommendations.map(r=>`<li style="margin-bottom:6px;font-size:13px">${r}</li>`).join('')}</ol>` : ''}` : ''}

<div class="footer">
  <span>Rapport généré par <strong>${agency}</strong>${prefs.agencyPhone?' · '+prefs.agencyPhone:''}${prefs.agencyWebsite?' · '+prefs.agencyWebsite:''}</span>
  <span>${new Date().toLocaleDateString('fr-CA',{year:'numeric',month:'long',day:'numeric'})}</span>
</div>
</body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  }

  const inp = { background:'#0a0a0d', border:'1px solid rgba(255,255,255,0.1)', borderRadius:10, padding:'13px 16px', color:'#EAEAEA', fontSize:'0.92rem', outline:'none', flex:1, boxSizing:'border-box' };

  return (
    <div style={{ padding:32, maxWidth:900 }}>

      {/* Header */}
      <div style={{ marginBottom:22 }}>
        <div style={{ fontSize:'0.63rem', color:'#333', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:6 }}>Ucreative</div>
        <h1 style={{ fontSize:'2rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.04em', margin:'0 0 6px' }}>Outil SEO</h1>
        <p style={{ color:'#333', fontSize:'0.83rem', margin:0 }}>Analyse complète d'un site web — score, technique, suggestions IA et rapport PDF</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:28, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:4, width:'fit-content' }}>
        {[['tool','🔍 Outil SEO'],['activity','📊 Activité']].map(([tab, label]) => (
          <button key={tab} onClick={() => switchTab(tab)}
            style={{ background: viewTab === tab ? '#C8FF00' : 'transparent', color: viewTab === tab ? '#000' : '#555',
              border:'none', borderRadius:7, padding:'7px 18px', fontWeight:800, fontSize:'0.8rem', cursor:'pointer', transition:'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── ACTIVITÉ ── */}
      {viewTab === 'activity' && (
        <div>
          {actLoading && <div style={{ color:'#444', fontSize:'0.85rem', padding:20 }}>Chargement…</div>}
          {activity?.error && <div style={{ color:'#f87171', fontSize:'0.85rem' }}>⚠️ {activity.error}</div>}
          {activity && !activity.error && (() => {
            const revenueWebsites = activity.websites.reduce((s, o) => s + (o.amount || 0), 0);
            const revenueAudits   = activity.audits.reduce((s, o) => s + (o.amount || 0), 0);
            const totalRevenue    = revenueWebsites + revenueAudits;
            const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('fr-CA') : '—';
            const fmtAmt  = n => n ? `${n.toLocaleString('fr-CA', { minimumFractionDigits: 2 })} $` : '—';
            const card = { background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px', marginBottom:18 };
            const th   = { color:'#333', fontSize:'0.65rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', padding:'8px 10px', borderBottom:'1px solid rgba(255,255,255,0.05)', textAlign:'left' };
            const td   = { color:'#888', fontSize:'0.78rem', padding:'9px 10px', borderBottom:'1px solid rgba(255,255,255,0.03)', verticalAlign:'top' };
            return (
              <>
                {/* KPIs */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:22 }}>
                  {[
                    { label:'Sites vendus',   value: activity.websites.length,  sub: fmtAmt(revenueWebsites), color:'#C8FF00' },
                    { label:'Audits payants', value: activity.audits.length,    sub: fmtAmt(revenueAudits),   color:'#a78bfa' },
                    { label:'Scans gratuits', value: activity.scans.length,     sub: 'derniers 200',          color:'#63b3ed' },
                  ].map(k => (
                    <div key={k.label} style={{ background:'#0d0d10', border:`1px solid ${k.color}18`, borderRadius:12, padding:'16px 20px' }}>
                      <div style={{ fontSize:'0.62rem', color:'#444', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>{k.label}</div>
                      <div style={{ fontSize:'1.8rem', fontWeight:900, color:k.color, lineHeight:1 }}>{k.value}</div>
                      <div style={{ fontSize:'0.72rem', color:'#333', marginTop:4 }}>{k.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Revenu total */}
                <div style={{ background:'rgba(200,255,0,0.04)', border:'1px solid rgba(200,255,0,0.12)', borderRadius:12, padding:'14px 20px', marginBottom:22, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ color:'#C8FF00', fontSize:'0.75rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>Revenu total (Stripe)</span>
                  <span style={{ color:'#C8FF00', fontSize:'1.4rem', fontWeight:900 }}>{fmtAmt(totalRevenue)}</span>
                </div>

                {/* Ventes sites web */}
                <div style={card}>
                  <div style={{ fontSize:'0.65rem', color:'#C8FF00', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:14 }}>🌐 Commandes sites web</div>
                  {activity.websites.length === 0
                    ? <div style={{ color:'#2a2a2a', fontSize:'0.8rem', fontStyle:'italic' }}>Aucune commande pour l'instant</div>
                    : <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <thead><tr>
                          <th style={th}>Nom</th><th style={th}>Courriel</th><th style={th}>Téléphone</th><th style={th}>Montant</th><th style={th}>Date</th><th style={th}>Statut</th>
                        </tr></thead>
                        <tbody>
                          {activity.websites.map(o => (
                            <tr key={o.id}>
                              <td style={td}><span style={{ color:'#EAEAEA', fontWeight:700 }}>{o.name}</span></td>
                              <td style={td}>{o.email}</td>
                              <td style={td}>{o.phone || '—'}</td>
                              <td style={{ ...td, color:'#C8FF00', fontWeight:700 }}>{fmtAmt(o.amount)}</td>
                              <td style={td}>{fmtDate(o.createdAt)}</td>
                              <td style={td}><span style={{ background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', color:'#4ade80', borderRadius:6, padding:'2px 8px', fontSize:'0.68rem', fontWeight:700 }}>{o.status || 'paid'}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                  }
                </div>

                {/* Audits payants */}
                <div style={card}>
                  <div style={{ fontSize:'0.65rem', color:'#a78bfa', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:14 }}>📋 Audits SEO payants</div>
                  {activity.audits.length === 0
                    ? <div style={{ color:'#2a2a2a', fontSize:'0.8rem', fontStyle:'italic' }}>Aucun audit payant pour l'instant</div>
                    : <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <thead><tr>
                          <th style={th}>URL</th><th style={th}>Courriel</th><th style={th}>Plan</th><th style={th}>Score</th><th style={th}>Montant</th><th style={th}>Date</th>
                        </tr></thead>
                        <tbody>
                          {activity.audits.map(o => (
                            <tr key={o.id}>
                              <td style={{ ...td, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                <span style={{ color:'#EAEAEA', fontSize:'0.75rem' }}>{o.url}</span>
                              </td>
                              <td style={td}>{o.email}</td>
                              <td style={td}><span style={{ background: o.plan==='pro' ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.05)', color: o.plan==='pro' ? '#a78bfa' : '#555', borderRadius:5, padding:'2px 7px', fontSize:'0.68rem', fontWeight:700 }}>{o.plan || 'essential'}</span></td>
                              <td style={{ ...td, color: o.score>=70?'#4ade80':o.score>=40?'#fbbf24':'#f87171', fontWeight:700 }}>{o.score}/100</td>
                              <td style={{ ...td, color:'#a78bfa', fontWeight:700 }}>{fmtAmt(o.amount)}</td>
                              <td style={td}>{fmtDate(o.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                  }
                </div>

                {/* Scans gratuits */}
                <div style={card}>
                  <div style={{ fontSize:'0.65rem', color:'#63b3ed', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:14 }}>🔍 Scans gratuits (leads potentiels)</div>
                  {activity.scans.length === 0
                    ? <div style={{ color:'#2a2a2a', fontSize:'0.8rem', fontStyle:'italic' }}>Aucun scan gratuit enregistré</div>
                    : <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <thead><tr>
                          <th style={th}>URL scannée</th><th style={th}>Score</th><th style={th}>Critiques</th><th style={th}>Importants</th><th style={th}>Date</th>
                        </tr></thead>
                        <tbody>
                          {activity.scans.map(o => (
                            <tr key={o.id}>
                              <td style={{ ...td, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                <span style={{ color:'#EAEAEA', fontSize:'0.75rem' }}>{o.url}</span>
                              </td>
                              <td style={{ ...td, color: o.score>=70?'#4ade80':o.score>=40?'#fbbf24':'#f87171', fontWeight:700 }}>{o.score}/100</td>
                              <td style={{ ...td, color: o.critiques>0?'#f87171':'#555' }}>{o.critiques}</td>
                              <td style={{ ...td, color: o.importants>0?'#fbbf24':'#555' }}>{o.importants}</td>
                              <td style={td}>{fmtDate(o.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                  }
                </div>
              </>
            );
          })()}
        </div>
      )}

      {viewTab === 'tool' && (<>

      {/* Input */}
      <div style={{ display:'flex', gap:10, marginBottom:28 }}>
        <input style={inp} value={url} onChange={e => setUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && analyze()}
          placeholder="https://exemple.com ou exemple.com" />
        <button onClick={analyze} disabled={loading || !url.trim()}
          style={{ background: loading || !url.trim() ? '#111' : '#C8FF00', color: loading || !url.trim() ? '#333' : '#000',
            border:'none', borderRadius:10, padding:'13px 28px', fontWeight:900, fontSize:'0.9rem', cursor: loading || !url.trim() ? 'default' : 'pointer', whiteSpace:'nowrap', transition:'all 0.15s' }}>
          {loading ? '⏳ Analyse...' : '🔍 Analyser'}
        </button>
      </div>

      {loading && (
        <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'28px 24px', textAlign:'center' }}>
          <div style={{ fontSize:'1.5rem', marginBottom:12, animation:'pulse 1.5s infinite' }}>🔍</div>
          <div style={{ color:'#EAEAEA', fontWeight:700, marginBottom:6 }}>Analyse en cours...</div>
          <div style={{ color:'#444', fontSize:'0.8rem', lineHeight:1.8 }}>
            Chargement de la page · Analyse HTML · Test PageSpeed Google · Suggestions IA
          </div>
          <div style={{ color:'#2a2a2a', fontSize:'0.72rem', marginTop:8 }}>Peut prendre jusqu'à 20 secondes (PageSpeed)</div>
        </div>
      )}

      {error && (
        <div style={{ background:'rgba(248,113,113,0.06)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:12, padding:'16px 20px' }}>
          <div style={{ color:'#f87171', fontWeight:700 }}>⚠️ Erreur</div>
          <div style={{ color:'#f87171cc', fontSize:'0.82rem', marginTop:4 }}>{error}</div>
        </div>
      )}

      {data && !loading && (() => {
        const d = data;
        const psM = d.pagespeed?.mobile;
        const psD = d.pagespeed?.desktop;
        const scoreColor = d.score >= 70 ? '#4ade80' : d.score >= 40 ? '#fbbf24' : '#f87171';

        const checks = [
          { ok: d.isHttps, label:'HTTPS sécurisé', detail: d.isHttps ? null : 'Site non sécurisé — pénalisé par Google' },
          { ok: d.title && d.titleLen>=40 && d.titleLen<=70, warn: !!d.title, label:'Balise titre', detail: d.title ? `"${d.title.slice(0,60)}" (${d.titleLen} car. — idéal: 40–70)` : 'Absente — critique pour le référencement' },
          { ok: d.metaDesc && d.metaLen>=100 && d.metaLen<=160, warn: !!d.metaDesc, label:'Meta description', detail: d.metaDesc ? `${d.metaLen} car. (idéal: 100–160)` : 'Absente — Google génère son propre extrait' },
          { ok: d.h1s?.length===1, warn: d.h1s?.length>1, label:'Balise H1', detail: d.h1s?.length===0 ? 'Absente' : d.h1s?.length>1 ? `${d.h1s.length} H1 trouvés — doit être unique` : `"${d.h1s[0]?.slice(0,50)}"` },
          { ok: d.imgTotal===0||d.imgWithAlt/d.imgTotal>=0.9, label:`Images alt (${d.imgWithAlt}/${d.imgTotal})`, detail: d.imgNoAlt?.length ? `Sans alt : ${d.imgNoAlt.join(', ')}` : null },
          { ok: d.hasViewport, label:'Adapté mobile (viewport)', detail: d.hasViewport ? null : 'Balise viewport absente' },
          { ok: !!d.canonical, label:'URL canonique', detail: d.canonical ? d.canonical.slice(0,60) : 'Absente — risque de duplicate content' },
          { ok: d.hasOGTitle && d.hasOGDesc, warn: d.hasOGTitle && !d.hasOGDesc, label:'Open Graph (réseaux sociaux)', detail: !d.hasOGTitle ? 'Absent — liens partagés sans aperçu' : !d.hasOGDesc ? 'Titre OK mais description manquante' : 'Configuré' },
          { ok: d.hasSchema, label:'Données structurées', detail: d.hasSchema ? 'Détecté' : 'Absent — moins de rich snippets dans Google' },
          { ok: d.hasAnalytics, label:'Google Analytics / GTM', detail: d.hasAnalytics ? 'Installé' : 'Absent — impossible de mesurer le trafic' },
          { ok: d.hasRobots, label:'Robots.txt', detail: d.hasRobots ? 'Présent' : 'Absent' },
          { ok: d.hasSitemap, label:'Sitemap XML', detail: d.hasSitemap ? 'Présent' : 'Absent — certaines pages mal indexées' },
        ];
        const passed = checks.filter(c => c.ok).length;

        return (
          <>
            {/* Score + actions */}
            <div style={{ background:'#0d0d10', border:`1px solid ${scoreColor}22`, borderRadius:14, padding:'24px 28px', marginBottom:14, display:'flex', alignItems:'center', gap:28, flexWrap:'wrap' }}>
              <ScoreCircle score={d.score} />
              <div style={{ flex:1, minWidth:200 }}>
                <div style={{ color:'#444', fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4 }}>Score SEO global</div>
                <div style={{ color:'#EAEAEA', fontWeight:800, fontSize:'1.05rem', marginBottom:4, wordBreak:'break-all' }}>{d.url}</div>
                <div style={{ color:'#555', fontSize:'0.8rem', marginBottom:12 }}>{passed}/{checks.length} critères validés</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button onClick={exportPDF}
                    style={{ background:'#C8FF00', color:'#000', border:'none', borderRadius:8, padding:'9px 18px', fontWeight:900, fontSize:'0.82rem', cursor:'pointer' }}>
                    📄 PDF
                  </button>
                  <button onClick={() => setShowFixes(v => !v)}
                    style={{ background: showFixes ? 'rgba(251,191,36,0.12)' : 'rgba(251,191,36,0.07)', border:`1px solid ${showFixes ? 'rgba(251,191,36,0.4)' : 'rgba(251,191,36,0.2)'}`, color:'#fbbf24', borderRadius:8, padding:'9px 16px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem' }}>
                    🔧 Corrections
                  </button>
                  <button onClick={() => { navigator.clipboard?.writeText(`Score SEO : ${d.score}/100\n${d.url}\n${checks.filter(c=>!c.ok).map(c=>`❌ ${c.label}`).join('\n')}`); setCopied(true); setTimeout(()=>setCopied(false),2000); }}
                    style={{ background:'transparent', border:'1px solid rgba(255,255,255,0.1)', color: copied ? '#4ade80' : '#555', borderRadius:8, padding:'9px 16px', cursor:'pointer', fontSize:'0.82rem' }}>
                    {copied ? '✓ Copié' : '📋 Copier'}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>

              {/* Audit technique */}
              <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
                <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:14 }}>Audit technique</div>
                {checks.map((c, i) => <Check key={i} ok={c.ok} warn={c.warn} label={c.label} detail={c.detail} />)}
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

                {/* PageSpeed */}
                {(psM || psD) && (
                  <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
                    <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:16 }}>⚡ PageSpeed Google</div>
                    <div style={{ display:'flex', gap:16, marginBottom:14 }}>
                      {psM && <PSBar score={psM.score} label="📱 Mobile" />}
                      {psD && <PSBar score={psD.score} label="💻 Desktop" />}
                    </div>
                    {psM && (psM.fcp || psM.lcp) && (
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:8, marginTop:10 }}>
                        {[['FCP',psM.fcp],['LCP',psM.lcp],['CLS',psM.cls],['TBT',psM.tbt]].filter(([,v])=>v).map(([k,v])=>(
                          <div key={k} style={{ background:'rgba(255,255,255,0.03)', borderRadius:7, padding:'8px 10px', textAlign:'center' }}>
                            <div style={{ color:'#444', fontSize:'0.6rem', fontWeight:700, textTransform:'uppercase', marginBottom:2 }}>{k}</div>
                            <div style={{ color:'#888', fontSize:'0.78rem', fontWeight:700 }}>{v}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {psM?.opportunities?.length > 0 && (
                      <div style={{ marginTop:12 }}>
                        <div style={{ fontSize:'0.62rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Optimisations suggérées</div>
                        {psM.opportunities.map((o,i) => <div key={i} style={{ color:'#555', fontSize:'0.72rem', padding:'3px 0' }}>→ {o}</div>)}
                      </div>
                    )}
                  </div>
                )}

                {/* Structure H1/H2 */}
                {(d.h1s?.length > 0 || d.h2s?.length > 0) && (
                  <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
                    <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:14 }}>Structure des titres</div>
                    {d.h1s?.map((h,i) => <div key={i} style={{ color:'#EAEAEA', fontSize:'0.82rem', fontWeight:700, padding:'4px 0', borderLeft:'3px solid #C8FF00', paddingLeft:10, marginBottom:4 }}>H1: {h.slice(0,60)}</div>)}
                    {d.h2s?.slice(0,4).map((h,i) => <div key={i} style={{ color:'#888', fontSize:'0.75rem', padding:'3px 0', borderLeft:'2px solid rgba(255,255,255,0.1)', paddingLeft:10, marginBottom:3 }}>H2: {h}</div>)}
                  </div>
                )}

                {/* Mots-clés extraits */}
                {d.keywords?.length > 0 && (
                  <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
                    <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:12 }}>Mots-clés détectés</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {d.keywords.map((k,i) => (
                        <span key={i} style={{ background:'rgba(200,255,0,0.07)', border:'1px solid rgba(200,255,0,0.2)', color:'#C8FF00', padding:'3px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:600 }}>{k}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Corrections rapides */}
            {showFixes && (() => {
              const fixes = generateFixes(d);
              const head  = generateCompleteHead(d);
              if (fixes.length === 0) return (
                <div style={{ background:'rgba(74,222,128,0.06)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:14, padding:'20px 24px', marginBottom:14, color:'#4ade80', fontWeight:700 }}>
                  ✅ Aucune correction technique nécessaire — excellent !
                </div>
              );
              return (
                <div style={{ background:'#090b08', border:'1px solid rgba(251,191,36,0.2)', borderRadius:14, overflow:'hidden', marginBottom:14 }}>
                  <div style={{ padding:'14px 22px', borderBottom:'1px solid rgba(251,191,36,0.12)', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                    <span style={{ color:'#fbbf24', fontSize:'0.72rem', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase' }}>🔧 {fixes.length} correction{fixes.length>1?'s':''} à appliquer</span>
                    <button onClick={() => { navigator.clipboard?.writeText(head); setCopiedFix('head'); setTimeout(()=>setCopiedFix(null),2500); }}
                      style={{ background: copiedFix==='head' ? 'rgba(74,222,128,0.1)' : '#C8FF00', color: copiedFix==='head' ? '#4ade80' : '#000', border: copiedFix==='head' ? '1px solid rgba(74,222,128,0.3)' : 'none', borderRadius:8, padding:'7px 16px', cursor:'pointer', fontWeight:900, fontSize:'0.78rem' }}>
                      {copiedFix==='head' ? '✓ Copié !' : '📋 Copier le <head> complet optimisé'}
                    </button>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column' }}>
                    {fixes.map((fix, i) => (
                      <div key={fix.key} style={{ padding:'16px 22px', borderBottom: i < fixes.length-1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                          <span style={{ color:'#EAEAEA', fontWeight:700, fontSize:'0.82rem' }}>{fix.icon} {fix.label}</span>
                          <button onClick={() => { navigator.clipboard?.writeText(fix.code); setCopiedFix(fix.key); setTimeout(()=>setCopiedFix(null),2000); }}
                            style={{ background:'transparent', border:`1px solid ${copiedFix===fix.key ? 'rgba(74,222,128,0.3)' : 'rgba(251,191,36,0.25)'}`, color: copiedFix===fix.key ? '#4ade80' : '#fbbf24', borderRadius:6, padding:'4px 12px', cursor:'pointer', fontSize:'0.72rem', fontWeight:700, flexShrink:0, transition:'all 0.15s' }}>
                            {copiedFix===fix.key ? '✓ Copié' : 'Copier le code'}
                          </button>
                        </div>
                        <pre style={{ background:'#0a0c09', border:'1px solid rgba(255,255,255,0.06)', borderRadius:8, padding:'12px 14px', fontSize:'0.72rem', color:'#4ade80', overflowX:'auto', margin:'0 0 8px', whiteSpace:'pre-wrap', wordBreak:'break-all', fontFamily:'monospace', lineHeight:1.7 }}>{fix.code}</pre>
                        <div style={{ color:'#444', fontSize:'0.7rem', lineHeight:1.6 }}>📌 {fix.where}</div>
                      </div>
                    ))}
                  </div>
                  {/* Head complet */}
                  <div style={{ padding:'16px 22px', background:'rgba(200,255,0,0.03)', borderTop:'1px solid rgba(200,255,0,0.08)' }}>
                    <div style={{ color:'#C8FF00', fontSize:'0.72rem', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:10 }}>⚡ &lt;head&gt; complet optimisé — prêt à copier-coller</div>
                    <pre style={{ background:'#080a07', border:'1px solid rgba(200,255,0,0.1)', borderRadius:8, padding:'14px 16px', fontSize:'0.7rem', color:'#888', overflowX:'auto', whiteSpace:'pre-wrap', fontFamily:'monospace', lineHeight:1.8, maxHeight:280, overflowY:'auto' }}>{head}</pre>
                    <div style={{ color:'#333', fontSize:'0.7rem', marginTop:8 }}>{'💡 Collez ce bloc dans le <head> de votre HTML. Ajustez l\'ID Google Analytics et l\'image Open Graph selon votre site.'}</div>
                  </div>
                </div>
              );
            })()}

            {/* Suggestions IA */}
            {d.suggestions && (
              <div style={{ background:'#080810', border:'1px solid rgba(139,92,246,0.2)', borderRadius:14, overflow:'hidden' }}>
                <div style={{ padding:'14px 22px', borderBottom:'1px solid rgba(139,92,246,0.1)' }}>
                  <span style={{ color:'#a78bfa', fontSize:'0.72rem', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase' }}>✨ Suggestions générées par Claude IA</span>
                </div>
                <div style={{ padding:'20px 22px', display:'flex', flexDirection:'column', gap:16 }}>
                  <div>
                    <div style={{ fontSize:'0.62rem', color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Titre optimisé</div>
                    <div style={{ background:'rgba(139,92,246,0.07)', border:'1px solid rgba(139,92,246,0.15)', borderRadius:8, padding:'10px 14px', color:'#EAEAEA', fontSize:'0.88rem', fontWeight:600 }}>{d.suggestions.optimizedTitle}</div>
                    <div style={{ color:'#333', fontSize:'0.68rem', marginTop:4 }}>{d.suggestions.optimizedTitle?.length} caractères</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'0.62rem', color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Meta description optimisée</div>
                    <div style={{ background:'rgba(139,92,246,0.07)', border:'1px solid rgba(139,92,246,0.15)', borderRadius:8, padding:'10px 14px', color:'#EAEAEA', fontSize:'0.85rem', lineHeight:1.6 }}>{d.suggestions.optimizedMeta}</div>
                    <div style={{ color:'#333', fontSize:'0.68rem', marginTop:4 }}>{d.suggestions.optimizedMeta?.length} caractères</div>
                  </div>
                  {d.suggestions.keywords?.length > 0 && (
                    <div>
                      <div style={{ fontSize:'0.62rem', color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Mots-clés cibles</div>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                        {d.suggestions.keywords.map((k,i) => <span key={i} style={{ background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.25)', color:'#a78bfa', padding:'4px 12px', borderRadius:20, fontSize:'0.78rem', fontWeight:700 }}>{k}</span>)}
                      </div>
                    </div>
                  )}
                  {d.suggestions.recommendations?.length > 0 && (
                    <div>
                      <div style={{ fontSize:'0.62rem', color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Recommandations prioritaires</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        {d.suggestions.recommendations.map((r,i) => (
                          <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                            <span style={{ color:'#a78bfa', fontWeight:900, fontSize:'0.8rem', flexShrink:0, marginTop:1 }}>{i+1}.</span>
                            <span style={{ color:'#888', fontSize:'0.8rem', lineHeight:1.5 }}>{r}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        );
      })()}

      </>)}

    </div>
  );
}
